import {memo, useRef, useState} from 'react'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {ProgressivePullRequestDiffEntry} from './progressive-diff/ProgressivePullRequestDiffEntry'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {useActiveChangeGroup, useDiffEntriesWithFilter, useDiffSummaryCount} from '../stores/selectors'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import styles from './PullRequestDiffsList.module.css'
export interface PullRequestDiffsListProps {
  basePath: string
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  basePath,
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const diffsParentRef = useRef<HTMLDivElement>(null)
  const diffEntries = useDiffEntriesWithFilter(filteredDiffSummaries)
  const activeChangeGroup = useActiveChangeGroup()

  const {onScrollToAndFocusEntry, getTargetableEntriesMap, selectedPathDigest, isSelectingLineOrRange} =
    useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()

  const totalVisibleEntries = diffEntries.length
  const shouldShowWhimsy = totalVisibleEntries >= 15

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      {diffEntries.map(progressiveDiffEntry => {
        return (
          <div
            key={progressiveDiffEntry.pathDigest}
            className={styles.DiffEntry}
            ref={(node: HTMLDivElement) => {
              const map = getTargetableEntriesMap()
              if (node) {
                map.set(progressiveDiffEntry.pathDigest, node)
              } else {
                map.delete(progressiveDiffEntry.pathDigest)
              }
            }}
          >
            <ProgressivePullRequestDiffEntry
              basePath={basePath}
              currentUser={currentUser}
              diffManuallyExpanded={diffManuallyExpanded}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              isSelected={
                selectedPathDigest === undefined ? undefined : progressiveDiffEntry.pathDigest === selectedPathDigest
              }
              isSelectingLineOrRange={isSelectingLineOrRange}
              onScrollToAndFocusEntry={onScrollToAndFocusEntry}
              progressiveDiffEntry={progressiveDiffEntry}
              repository={repository}
            />
          </div>
        )
      })}
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

try{ PullRequestDiffsList.displayName ||= 'PullRequestDiffsList' } catch {}
try{ PullRequestDiffsListUnmemoized.displayName ||= 'PullRequestDiffsListUnmemoized' } catch {}